import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { FlaskConical, CircleCheck, Clock, HelpCircle } from "lucide-react";

function Row({ label, children }) {
  return (
    <div className="flex items-baseline gap-3 py-1.5 border-b border-[#1c1c1f] last:border-b-0">
      <span className="text-[11px] uppercase tracking-widest text-[#71717A] font-mono w-56 shrink-0">{label}</span>
      <span className="text-sm font-mono text-[#E4E4E7]">{children}</span>
    </div>
  );
}

export default function ExperimentalSources() {
  const [d, setD] = useState(null);
  const [tk, setTk] = useState(null);
  useEffect(() => {
    api.get("/experimental/kickstarter").then((r) => setD(r.data));
    api.get("/experimental/tiktok").then((r) => setTk(r.data)).catch(() => {});
  }, []);
  if (!d) return <div className="p-8 text-[#71717A] font-mono text-sm">Loading…</div>;

  const cfg = d.config || {};
  const pc = d.provider_compatibility || {};
  const canary = d.canary || {};
  const probe = d.probe;                 // null => no local record
  const cred = d.credential || {};
  const compatPass = pc.status === "PASS";
  const baseline = cfg.amazon_nr_baseline_pct;

  return (
    <div className="p-8 max-w-[1100px]" data-testid="experimental-sources">
      <div className="mb-1 flex items-center gap-2">
        <FlaskConical className="text-sky-400" size={22} />
        <h1 className="font-head text-3xl font-bold tracking-tight">Future Product Signals</h1>
      </div>
      <p className="text-[#71717A] text-sm mb-2">Early Innovation Radar — secondary intelligence, not the live commercial radar.</p>
      <p className="text-[11px] text-amber-300/80 font-mono mb-6 max-w-3xl border-l-2 border-amber-500/40 pl-3">
        Crowdfunding projects are early product intelligence only. They may be proprietary, unavailable for wholesale,
        difficult to manufacture, or unsuitable for immediate ecommerce launch. Not part of Amazon Current Signal.
      </p>

      <div data-testid="experimental-kickstarter-panel" className="border border-[#27272A] bg-[#0c0c0e]">
        {/* header + at-a-glance status */}
        <div className="px-5 py-4 border-b border-[#27272A]">
          <div className="flex items-center gap-2 mb-4">
            <h2 className="font-head text-lg font-semibold">Kickstarter</h2>
            <span className="text-[11px] font-mono px-2 py-0.5 border text-sky-300 bg-sky-400/10 border-sky-400/30">Experimental</span>
            <span className="ml-auto text-[11px] font-mono text-[#52525B]" data-testid="ks-environment">Environment: {d.environment}</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="border border-[#27272A] bg-[#18181B] p-3" data-testid="ks-provider-compat">
              <div className="text-[10px] uppercase tracking-widest text-[#71717A]">Provider compatibility</div>
              <div className={`mt-1 text-sm font-mono flex items-center gap-1.5 ${compatPass ? "text-emerald-300" : "text-[#A1A1AA]"}`}>
                {compatPass ? <><CircleCheck size={14} /> PASS {pc.provenance ? `(${pc.provenance})` : ""}</>
                  : <><HelpCircle size={14} className="text-[#71717A]" /> No local record</>}
              </div>
            </div>
            <div className="border border-[#27272A] bg-[#18181B] p-3" data-testid="ks-canary-status">
              <div className="text-[10px] uppercase tracking-widest text-[#71717A]">Quality canary</div>
              <div className="mt-1 text-sm font-mono flex items-center gap-1.5 text-amber-300">
                {canary.status === "NOT_RUN"
                  ? <><Clock size={14} /> Not run</>
                  : <><CircleCheck size={14} className="text-emerald-300" /> {canary.status}</>}
              </div>
            </div>
            <div className="border border-[#27272A] bg-[#18181B] p-3">
              <div className="text-[10px] uppercase tracking-widest text-[#71717A]">Live collection</div>
              <div className="mt-1 text-sm font-mono text-rose-300">{cred.live_authorization || "OFF"}</div>
            </div>
            <div className="border border-[#27272A] bg-[#18181B] p-3">
              <div className="text-[10px] uppercase tracking-widest text-[#71717A]">Novel/useful yield</div>
              <div className="mt-1 text-sm font-mono text-emerald-300">
                {canary.genuinely_novel_useful_pct != null ? `${canary.genuinely_novel_useful_pct}%` : "Awaiting canary"}
              </div>
            </div>
          </div>
        </div>

        <div className="px-5 py-4 grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Configuration (always present) */}
          <div>
            <div className="text-xs font-mono text-[#A1A1AA] mb-2">Experiment configuration</div>
            <Row label="Source">{cfg.source}</Row>
            <Row label="Provider">{cfg.provider}</Row>
            <Row label="Underlying platform">{cfg.underlying_platform}</Row>
            <Row label="Evidence class">{cfg.evidence_class}</Row>
            <Row label="IP status">{cfg.ip_status}</Row>
            <Row label="Target accepted">{cfg.target_accepted}</Row>
            <Row label="Hard raw ceiling">{cfg.hard_raw_ceiling}</Row>
            <Row label="Amazon NR baseline"><span data-testid="ks-baseline">{baseline != null ? `${baseline}%` : "6.1%"}</span></Row>
          </div>

          {/* This environment (stored results + credential) */}
          <div>
            <div className="text-xs font-mono text-[#A1A1AA] mb-2">This environment</div>
            <Row label="Environment">{d.environment}</Row>
            <Row label="Compatibility probe data">
              {probe ? `Present · ${probe.returned_item_count} projects (${probe.product_design_count} Product Design)` : "Not present in this environment"}
            </Row>
            <Row label="Quality canary">{canary.status === "NOT_RUN" ? "NOT RUN" : canary.status}</Row>
            <Row label="Preserved projects">{d.projects_count}</Row>
            <Row label="Apify credential">{cred.apify_configured ? "Configured" : "Not configured"}</Row>
            <Row label="Live canary authorization">{cred.live_authorization || "OFF"}</Row>
            <Row label="Kickstarter scheduler">OFF</Row>
            <Row label="Sourceability">{(d.commercial_status || {}).sourceability_status || "NOT_RESEARCHED"}</Row>
            <Row label="IP status">{(d.commercial_status || {}).ip_status || "UNKNOWN"}</Row>
            <Row label="Amazon confirmation">{(d.commercial_status || {}).amazon_confirmation || "NOT_CHECKED"}</Row>
            <Row label="Google confirmation">{(d.commercial_status || {}).google_confirmation || "NOT_CHECKED"}</Row>
            <Row label="Social confirmation">{(d.commercial_status || {}).social_confirmation || "NOT_CHECKED"}</Row>
          </div>
        </div>

        {/* probe detail (only if present in this environment) */}
        {probe && (
          <div className="px-5 pb-4" data-testid="ks-probe-detail">
            <div className="text-xs font-mono text-[#A1A1AA] mb-2">Compatibility probe — {probe.status} {probe.apify_run_id ? `· run ${probe.apify_run_id}` : ""}</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
              {[["Returned", probe.returned_item_count], ["Product Design", probe.product_design_count],
                ["Physical", probe.physical_count], ["Wrong category", probe.wrong_category_excluded],
                ["Quarantined", probe.quarantined_count],
                ["Surface purity", probe.surface_purity_pct != null ? `${probe.surface_purity_pct}%` : "—"]].map(([l, v]) => (
                <div key={l} className="border border-[#27272A] bg-[#18181B] p-3">
                  <div className="font-mono text-lg font-bold text-[#F4F4F5]">{v ?? "—"}</div>
                  <div className="text-[10px] uppercase tracking-widest text-[#71717A]">{l}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* preserved projects table / empty state */}
        <div className="px-5 pb-4">
          <div className="text-[11px] uppercase tracking-widest text-[#71717A] font-mono mb-2">
            Preserved projects {d.projects_count ? `(${d.projects_count})` : ""}
          </div>
          {d.projects_count === 0 ? (
            <div className="border border-dashed border-[#27272A] p-4 text-center text-xs text-[#52525B] font-mono" data-testid="ks-projects-empty">
              No Kickstarter experimental projects stored in {d.environment === "PRODUCTION" ? "Production" : "this environment"}.
              <div className="mt-1 text-[10px]">Experimental data is environment-specific and is not part of the live Amazon Radar. Data was not lost.</div>
            </div>
          ) : (
            <div className="border border-[#27272A] overflow-x-auto">
              <table className="w-full text-sm min-w-[720px]">
                <thead>
                  <tr className="text-[11px] uppercase tracking-widest text-[#71717A] font-mono border-b border-[#27272A]">
                    <th className="text-left px-3 py-2">Product</th><th className="text-left px-3 py-2">Source</th>
                    <th className="text-left px-3 py-2">Category</th>
                    <th className="text-right px-3 py-2">Funding</th><th className="text-right px-3 py-2">Backers</th>
                    <th className="text-left px-3 py-2">Launch</th><th className="text-left px-3 py-2">Status</th>
                    <th className="text-left px-3 py-2">Original</th>
                  </tr>
                </thead>
                <tbody>
                  {d.projects.map((p, i) => (
                    <tr key={i} data-testid={`ks-project-${i}`} className="border-b border-[#1c1c1f]">
                      <td className="px-3 py-2 font-head">{p.title}</td>
                      <td className="px-3 py-2"><span className="text-[10px] font-mono px-1.5 py-0.5 border text-sky-300 bg-sky-400/10 border-sky-400/30">{p.source_platform || "KICKSTARTER"}</span></td>
                      <td className="px-3 py-2 text-xs text-[#A1A1AA]">{p.category}{p.in_scope === false ? " (excluded)" : ""}</td>
                      <td className="px-3 py-2 text-right font-mono text-xs">{p.pledged != null ? `${p.currency || ""} ${p.pledged}` : "—"}</td>
                      <td className="px-3 py-2 text-right font-mono text-xs">{p.backers ?? "—"}</td>
                      <td className="px-3 py-2 font-mono text-[11px]">{(p.launched_at || "").slice(0, 10) || "—"}</td>
                      <td className="px-3 py-2 text-xs">{p.state}</td>
                      <td className="px-3 py-2 text-[11px]">
                        {p.project_url
                          ? <a href={p.project_url} target="_blank" rel="noopener noreferrer" data-testid={`ks-link-${i}`}
                              className="text-emerald-400 hover:underline font-mono">View original ↗</a>
                          : <span className="text-[#52525B] font-mono">Original project link unavailable</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="px-5 pb-4 text-[10px] text-[#52525B] font-mono border-t border-[#1c1c1f] pt-3">
          Compatibility probe was performed in Preview; experimental probe data is not copied into Production.
          Experimental — not part of the live Radar. No Current Signal, Commerce, Launch or Cross-Source status is applied. Visibility only.
        </div>
      </div>

      {tk && <TikTokPanel tk={tk} />}
    </div>
  );
}

function StageChip({ stage, label }) {
  const tone = {
    MULTI_CREATOR_STRENGTHENING: "text-emerald-300 bg-emerald-400/10 border-emerald-400/30",
    MULTI_CREATOR_EMERGING: "text-sky-300 bg-sky-400/10 border-sky-400/30",
    SINGLE_CREATOR_CONCENTRATED: "text-amber-300 bg-amber-400/10 border-amber-400/30",
    SINGLE_VIDEO_WATCH: "text-zinc-300 bg-zinc-400/10 border-zinc-400/30",
    NO_SOCIAL_SIGNAL: "text-zinc-400 bg-zinc-500/10 border-zinc-500/30",
  }[stage] || "text-zinc-300 bg-zinc-400/10 border-zinc-400/30";
  return <span className={`text-[11px] font-mono px-2 py-0.5 border ${tone}`}>{label}</span>;
}

function Metric({ label, value }) {
  return (
    <div className="border border-[#27272A] bg-[#18181B] p-3">
      <div className="font-mono text-lg font-bold text-[#F4F4F5]">{value ?? "—"}</div>
      <div className="text-[10px] uppercase tracking-widest text-[#71717A] mt-1">{label}</div>
    </div>
  );
}

function ConceptCard({ c }) {
  const [open, setOpen] = useState(false);
  const t = c.trajectory || {};
  const o = t.observed || {};
  const calc = t.calculated || {};
  const conc = calc.top_creator_concentration != null ? `${Math.round(calc.top_creator_concentration * 100)}%` : "—";
  return (
    <div className="border border-[#27272A] bg-[#101013]" data-testid={`tk-concept-${c.concept.replace(/\s+/g, "-")}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex flex-wrap items-center gap-3 px-4 py-3 text-left">
        <span className="font-head text-base font-semibold capitalize">{c.concept}</span>
        <StageChip stage={t.stage} label={t.label} />
        <span className="text-[11px] font-mono text-[#71717A]">{o.distinct_creators} creators · {o.qualifying_videos} videos</span>
        {c.extraction_confidence !== "MEDIUM" && (
          <span className="text-[10px] font-mono px-1.5 py-0.5 border text-amber-300 bg-amber-400/10 border-amber-400/30">
            Extraction: {c.extraction_confidence}
          </span>
        )}
        <span className="ml-auto text-xs font-mono text-[#52525B]">{open ? "Hide" : "View"} trajectory</span>
      </button>

      {open && (
        <div className="px-4 pb-4 border-t border-[#1c1c1f] pt-3 space-y-4">
          {/* trajectory metrics */}
          <div>
            <div className="text-[11px] uppercase tracking-widest text-[#71717A] font-mono mb-2">
              Social trajectory ({t.version}) · <span className="text-amber-400/80">{t.calibration_status}</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
              <Metric label="Independent creators" value={o.distinct_creators} />
              <Metric label="Qualifying videos" value={o.qualifying_videos} />
              <Metric label="Observation dates" value={(o.observation_dates || []).length} />
              <Metric label="New videos / day" value={calc.appearance_velocity_per_day ?? "Insufficient"} />
              <Metric label="Top-creator share" value={conc} />
              <Metric label="Engagement" value={calc.engagement_trend} />
            </div>
          </div>

          {/* why / missing */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-emerald-400/70 font-mono mb-1">Why</div>
              <ul className="text-[#A1A1AA] text-xs space-y-0.5 list-disc list-inside">
                {(t.why || []).map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-amber-400/70 font-mono mb-1">What's missing</div>
              <ul className="text-[#A1A1AA] text-xs space-y-0.5 list-disc list-inside">
                {(t.missing || []).length ? (t.missing || []).map((m, i) => <li key={i}>{m}</li>) : <li>—</li>}
              </ul>
            </div>
          </div>

          {/* evidence status */}
          <div className="flex flex-wrap gap-2 text-[10px] font-mono">
            <span className="px-2 py-0.5 border border-[#27272A] text-[#A1A1AA]">Evidence: {c.evidence_class}</span>
            <span className="px-2 py-0.5 border border-[#27272A] text-[#A1A1AA]">IP: {c.ip_status}</span>
            <span className="px-2 py-0.5 border border-[#27272A] text-[#A1A1AA]">Sourceability: {c.sourceability_status}</span>
            <span className="px-2 py-0.5 border border-[#27272A] text-[#A1A1AA]">Amazon: {c.amazon_confirmation}</span>
          </div>

          {/* videos */}
          <div>
            <div className="text-[11px] uppercase tracking-widest text-[#71717A] font-mono mb-2">
              Videos ({c.videos.length}) — same creator counted once for diversity
            </div>
            <div className="border border-[#27272A] overflow-x-auto">
              <table className="w-full text-sm min-w-[680px]">
                <thead>
                  <tr className="text-[11px] uppercase tracking-widest text-[#71717A] font-mono border-b border-[#27272A]">
                    <th className="text-left px-3 py-2">Creator</th><th className="text-left px-3 py-2">Posted</th>
                    <th className="text-right px-3 py-2">Views</th><th className="text-right px-3 py-2">Likes</th>
                    <th className="text-left px-3 py-2">Seen on</th><th className="text-left px-3 py-2">Source</th>
                  </tr>
                </thead>
                <tbody>
                  {c.videos.map((v, i) => (
                    <tr key={i} className="border-b border-[#1c1c1f]" data-testid={`tk-video-${v.video_id}`}>
                      <td className="px-3 py-2 font-mono text-xs">@{v.author_username}</td>
                      <td className="px-3 py-2 font-mono text-[11px]">{(v.posted_at || "").slice(0, 10) || "—"}</td>
                      <td className="px-3 py-2 text-right font-mono text-xs">{v.views?.toLocaleString() ?? "—"}</td>
                      <td className="px-3 py-2 text-right font-mono text-xs">{v.likes?.toLocaleString() ?? "—"}</td>
                      <td className="px-3 py-2 font-mono text-[11px] text-[#71717A]">{(v.observation_dates || []).length}d</td>
                      <td className="px-3 py-2 text-[11px]">
                        {v.has_source_link
                          ? <a href={v.video_url} target="_blank" rel="noopener noreferrer" data-testid={`tk-link-${v.video_id}`}
                              className="text-emerald-400 hover:underline font-mono">View on TikTok ↗</a>
                          : <span className="text-[#52525B] font-mono">Link unavailable</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TikTokPanel({ tk }) {
  const cfg = tk.config || {};
  const cred = tk.credential || {};
  const concepts = tk.concepts || [];
  const review = tk.entity_review_queue || [];
  return (
    <div data-testid="experimental-tiktok-panel" className="border border-[#27272A] bg-[#0c0c0e] mt-6">
      <div className="flex flex-wrap items-center gap-3 px-5 py-4 border-b border-[#27272A]">
        <h2 className="font-head text-lg font-semibold">TikTok Organic</h2>
        <span className="text-[11px] font-mono px-2 py-0.5 border text-sky-300 bg-sky-400/10 border-sky-400/30">Primary discovery (building)</span>
        <span className="text-xs font-mono text-[#71717A]">{cfg.provider}</span>
      </div>

      {/* MOCK / OFFLINE banner */}
      <div className="mx-5 mt-4 border border-amber-500/40 bg-amber-500/5 px-4 py-2.5 flex items-center gap-2" data-testid="tk-mock-banner">
        <FlaskConical size={15} className="text-amber-400 shrink-0" />
        <span className="text-[11px] font-mono text-amber-200">
          MOCK / OFFLINE DEVELOPMENT DATA — synthetic fixtures, NOT live TikTok observations. Provider schema {cfg.schema_status}. No external call until final live validation.
        </span>
      </div>

      <div className="px-5 py-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        <Metric label="Product concepts" value={tk.concept_count} />
        <Metric label="Mock videos" value={tk.mock_videos} />
        <Metric label="Needs extraction review" value={review.length} />
        <Metric label="Probe status" value={tk.probe_status} />
        <Metric label="Live auth" value={cred.live_authorization} />
        <Metric label="Sourceability" value={cfg.sourceability_status} />
      </div>

      {/* concepts */}
      <div className="px-5 pb-4 space-y-2">
        <div className="text-[11px] uppercase tracking-widest text-[#71717A] font-mono mb-1">Product concepts (social trajectory)</div>
        {concepts.length === 0 ? (
          <div className="border border-dashed border-[#27272A] p-4 text-center text-xs text-[#52525B] font-mono" data-testid="tk-concepts-empty">
            No mock concepts loaded. An admin can load the offline fixture to develop the analyst view.
          </div>
        ) : concepts.map((c) => <ConceptCard key={c.concept} c={c} />)}
      </div>

      {/* extraction review */}
      {review.length > 0 && (
        <div className="px-5 pb-4">
          <div className="text-[11px] uppercase tracking-widest text-[#71717A] font-mono mb-2">
            Extraction review — captions with no confident product concept (never auto-merged)
          </div>
          <div className="space-y-1">
            {review.map((r) => (
              <div key={r.video_id} className="border border-[#27272A] bg-[#18181B] px-3 py-2 flex items-center gap-3 text-xs" data-testid={`tk-review-${r.video_id}`}>
                <span className="font-mono text-amber-300">{r.extraction_confidence}</span>
                <span className="text-[#A1A1AA] truncate">@{r.author_username}: "{r.caption}"</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="px-5 pb-4 text-[10px] text-[#52525B] font-mono border-t border-[#1c1c1f] pt-3">
        Experimental primary-discovery sensor. Measures MULTIPLE INDEPENDENT CREATORS demonstrating a product over time — never raw viral views.
        Not mixed into the Amazon radar, Current Signal, Commerce, Launch or Cross-Source. {tk.final_live_dependency}.
      </div>
    </div>
  );
}
